import React, { useState, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCompanyData } from '@/hooks/useCompanyData';

// Import refactored components and hooks
import { AssetImageEditorProps, AssetImageEditorRef } from './types';
import { useEditorState } from './hooks/useEditorState';
import { useAnnotationState } from './hooks/useAnnotationState';
import { useMaskState } from './hooks/useMaskState';
import { useCropState } from './hooks/useCropState';
import { useViewportEffects } from './hooks/useViewportEffects';
import { useEditorHandlers } from './hooks/useEditorHandlers';
import { useCanvaIntegration } from './hooks/useCanvaIntegration';
import { useSaveHandlers } from './hooks/useSaveHandlers';
import { EditorSidebar } from './components/EditorSidebar';
import { CanvasArea } from './components/CanvasArea';
import { LoadingOverlay } from './components/LoadingOverlay';
import { isCloudinaryUrl, getImageUrlSource } from './utils/cropUtils';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const AssetImageEditor = forwardRef<AssetImageEditorRef, AssetImageEditorProps>(({
  open,
  onOpenChange,
  asset,
  onSuccess,
  reopenCropEditor = false,
  editorMode = null,
  config
}, ref) => {
  // Initialize all state hooks
  const editorState = useEditorState(asset, config);
  const annotationState = useAnnotationState();
  const maskState = useMaskState();
  const cropState = useCropState();
  
  // Handle reopenCropEditor prop
  useEffect(() => {
    if (reopenCropEditor && open) {
      // Reset any active modes
      annotationState.setIsAnnotationMode(false);
      maskState.setIsMaskMode(false);
      // Ensure we're showing the editor interface
    }
  }, [reopenCropEditor, open, annotationState, maskState]);
  
  const { toast } = useToast();
  const { data: company, isLoading: companyLoading } = useCompanyData({ 
    slug: asset?.company_slug || asset?.slug 
  });

  // Get pageData with quarters and scrapes from company data
  const pageData = company?.pages?.find(page => page.id === asset?.page_id);
  const pageDataLoaded = !!pageData && !!pageData.quarters && !!pageData.scrapes;
  
  
  // Check if there are any URL validation errors
  const hasValidationError = Boolean(editorState.urlValidationError);

  // Canva loading state
  const [isCanvaLoading, setIsCanvaLoading] = useState<{ 
    type: 'primary' | 'secondary' | null; 
    message: string 
  }>({ type: null, message: '' });


  // Refs for image elements and dragging state
  const imageRef = useRef<HTMLImageElement>(null);
  const secondaryImageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSecondaryDraggingRef = useRef<boolean>(false);
  const secondaryDragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Custom hooks for functionality
  useViewportEffects(open, asset, editorState, containerRef);

  const handlers = useEditorHandlers(
    editorState,
    annotationState,
    maskState,
    cropState,
    asset,
    pageData,
    companyLoading,
    toast,
    isSecondaryDraggingRef,
    secondaryDragStartRef
  );

  const canvaIntegration = useCanvaIntegration(
    editorState,
    asset,
    imageRef,
    secondaryImageRef,
    toast,
    isCanvaLoading,
    setIsCanvaLoading
  );

  // Helper function to get offset coordinates
  const getOffsetCoordinates = useCallback((isPrimary: boolean = true) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return { x: 0, y: 0 };

    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;
    
    const coordinates = isPrimary ? editorState.zoomCoordinates : editorState.secondaryZoomCoordinates;
    
    return {
      x: containerCenterX - coordinates.x,
      y: containerCenterY - coordinates.y
    };
  }, [editorState]);


  // Helper functions for quarter data
  const getPrimaryQuarterData = useCallback(() => {
    return editorState.primaryImageData;
  }, [editorState]);

  const getSecondaryQuarterData = useCallback(() => {
    return editorState.secondaryImageData;
  }, [editorState]);

  // Get image URLs - always use original/base URLs for display in canvas
  // The pan/zoom is handled by CSS transforms, not by cropped URLs
  const primaryImageUrl = useMemo(() => {
    return canvaIntegration.getPrimaryDisplayImageUrl(asset);
  }, [canvaIntegration, asset, editorState.primaryImageMode, editorState.primaryImageData]);
  
  const secondaryImageUrl = useMemo(() => {
    return canvaIntegration.getSecondaryDisplayImageUrl(asset);
  }, [canvaIntegration, asset, editorState.secondaryImageMode]);

  const saveHandlers = useSaveHandlers(
    editorState,
    annotationState,
    asset,
    imageRef,
    secondaryImageRef,
    toast,
    onOpenChange,
    onSuccess
  );

  // Expose save functionality through ref
  useImperativeHandle(ref, () => ({
    save: async () => {
      await saveHandlers.handleSave();
    }
  }), [saveHandlers]);

  // Initialize editor mode on open
  useEffect(() => {
    if (open && editorMode) {
      if (editorMode.includes('dual') || editorMode.includes('side-by-side')) {
        handlers.handleComparisonModeChange('side_by_side');
      } else if (editorMode.includes('overlay')) {
        handlers.handleComparisonModeChange('overlay');
      }
    }
  }, [open, editorMode, handlers]);

  // Keyboard event handling for annotation deletion - React 18 compatible
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (annotationState.isAnnotationMode && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        e.stopPropagation();

        // Use requestAnimationFrame to ensure proper event handling in React 18
        requestAnimationFrame(() => {
          // Delete primary annotation if selected
          if (annotationState.selectedAnnotationId) {
            annotationState.deleteAnnotation(annotationState.selectedAnnotationId);
          }

          // Delete secondary annotation if selected
          if (annotationState.selectedSecondaryAnnotationId) {
            annotationState.deleteSecondaryAnnotation(annotationState.selectedSecondaryAnnotationId);
          }
        });
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown, { passive: false });
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, annotationState.isAnnotationMode, annotationState.selectedAnnotationId, annotationState.selectedSecondaryAnnotationId, annotationState.deleteAnnotation, annotationState.deleteSecondaryAnnotation]);

  // Handle dialog close
  const handleDialogClose = useCallback(() => {
    if (!editorState.isUploading) {
      onOpenChange(false);
    }
  }, [editorState.isUploading, onOpenChange]);


  if (!open && !editorState.isEmbedded) return null;

  const mainContent = (
    <>
      {/* Show error message for non-Cloudinary URLs */}
      {hasValidationError ? (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="max-w-2xl mx-auto p-8 text-center">
                <div className="mb-6">
                  <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Image Editor Not Available
                  </h2>
                  <p className="text-gray-600 mb-6">
                    The image editor requires Cloudinary URLs to perform cropping and editing operations. 
                    This asset contains images hosted on other services that are not compatible with the editor.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg border p-6 text-left mb-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Error Details:</h3>
                  <div className="p-3 bg-red-50 rounded-md">
                    <pre className="text-sm text-red-700 whitespace-pre-wrap font-mono">
                      {editorState.urlValidationError}
                    </pre>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-900 mb-2">💡 What you can do:</h4>
                  <ul className="text-sm text-blue-800 text-left space-y-1">
                    <li>• View the images directly using the "View Original" buttons below</li>
                    <li>• Contact support to migrate images to Cloudinary for editing capabilities</li>
                    <li>• Use external tools to edit images, then upload new versions</li>
                  </ul>
                </div>
                
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    onClick={() => onOpenChange(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Close Editor
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Normal editor interface */
            <div className="flex min-h-full h-full" ref={containerRef}>
              {!editorState.config?.hideLeftPanel && (
                <EditorSidebar
                  asset={asset}
                  editorState={editorState}
                  annotationState={annotationState}
                  maskState={maskState}
                  imageRef={imageRef}
                  secondaryImageRef={secondaryImageRef}
                  onAspectRatioChange={handlers.handleAspectRatioChange}
                  onComparisonModeChange={handlers.handleComparisonModeChange}
                  onSave={saveHandlers.handleSave}
                  onCancel={saveHandlers.handleCancel}
                  getOffsetCoordinates={getOffsetCoordinates}
                  pageData={pageData}
                  pageDataLoaded={pageDataLoaded}
                />
              )}
              
              <div className="flex-1 relative">
                <CanvasArea
                  asset={asset}
                  pageData={pageData}
                  pageDataLoaded={pageDataLoaded}
                  editorState={editorState}
                  config={config}
                  annotationState={annotationState}
                  maskState={maskState}
                  cropState={cropState}
                  imageRef={imageRef}
                  secondaryImageRef={secondaryImageRef}
                  isCanvaLoading={isCanvaLoading}
                  companyLoading={companyLoading}
                  containerRef={containerRef}
                  isSecondaryDraggingRef={isSecondaryDraggingRef}
                  imageUrl={primaryImageUrl}
                  secondaryImageUrl={secondaryImageUrl}
                  onVersionSelection={handlers.handleVersionSelection}
                  onMouseDown={handlers.handleMouseDown}
                  onMouseMove={handlers.handleMouseMove}
                  onMouseUp={handlers.handleMouseUp}
                  onWheel={handlers.handleWheel}
                  onSecondaryMouseDown={handlers.handleSecondaryMouseDown}
                  onSecondaryMouseMove={handlers.handleSecondaryMouseMove}
                  onSecondaryMouseUp={handlers.handleSecondaryMouseUp}
                  onSecondaryWheel={handlers.handleSecondaryWheel}
                  onCanvaEdit={canvaIntegration.handleCanvaEditWithCrop}
                  onZoom={handlers.handleZoom}
                  onReset={() => {}} 
                  onZoomPanLinkToggle={handlers.handleZoomPanLinkToggle}
                  onPrimaryReset={handlers.handlePrimaryReset}
                  onSecondaryReset={handlers.handleSecondaryReset}
                  getPrimaryDisplayImageUrl={canvaIntegration.getPrimaryDisplayImageUrl}
                  getSecondaryDisplayImageUrl={canvaIntegration.getSecondaryDisplayImageUrl}
                  getOffsetCoordinates={getOffsetCoordinates}
                  getPrimaryQuarterData={getPrimaryQuarterData}
                  getSecondaryQuarterData={getSecondaryQuarterData}
                />
                
                {editorState.isUploading && (
                  <LoadingOverlay isVisible={true} message="Saving changes..." />
                )}
              </div>
            </div>
          )}
    </>
  );

  // Return embedded version or modal version based on config
  if (editorState.isEmbedded) {
    return (
      <div className="w-full h-full min-h-full flex flex-col">
        {mainContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-full max-h-full h-screen w-screen p-0 gap-0">
        <DialogTitle className="sr-only">
          Asset Image Editor - {asset.slug || 'Unknown Company'}
        </DialogTitle>
        {mainContent}
      </DialogContent>
    </Dialog>
  );
});

AssetImageEditor.displayName = 'AssetImageEditor';